import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';

export default function Departments() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.departments.list({ per_page: 500 });
      const raw = data.departments || data.data || data;
      setRows(Array.isArray(raw) ? raw : raw?.data || []);
    } catch (err) {
      showError('Failed to load departments', extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', is_active: true });
    setErrors({});
    setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name, is_active: row.is_active });
    setErrors({});
    setModal(true);
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Department name is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.departments.update(editing.id, form);
        toast('success', '', 'Department updated');
      } else {
        await api.departments.create(form);
        toast('success', '', 'Department created');
      }
      setModal(false);
      load();
    } catch (err) {
      showError('Failed to save department', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.departments.update(row.id, { is_active: !row.is_active });
      toast('success', '', `Department ${row.is_active ? 'deactivated' : 'activated'}`);
      load();
    } catch (err) {
      showError('Failed to toggle status', extractError(err));
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete(`department "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.departments.remove(row.id);
      toast('success', '', 'Department deleted');
      load();
    } catch (err) {
      showError('Failed to delete department', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Departments"
        subtitle="Manage company departments."
        crumbs={[{ label: 'HR' }, { label: 'Departments' }]}
        actions={hasPermission('create-departments') && (
          <button className="btn btn-primary" onClick={openCreate}><FiPlus /> Add Department</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="departments" label="departments" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No departments found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Department', render: (r) => <b>{r.name}</b> },
              { key: 'employees_count', label: 'Employees', render: (r) => <span className="badge badge-indigo">{r.employees_count ?? 0}</span> },
              {
                key: 'is_active',
                label: 'Status',
                render: (r) => (
                  <button
                    className={`toggle-btn ${r.is_active ? 'active' : ''}`}
                    onClick={() => hasPermission('update-departments') && toggleActive(r)}
                    title={r.is_active ? 'Click to deactivate' : 'Click to activate'}
                    type="button"
                  >
                    <span className="toggle-knob" />
                  </button>
                ),
              },
              {
                key: 'id',
                label: '',
                align: 'right',
                render: (r) => (
                  <div className="actions">
                    {hasPermission('update-departments') && <button className="btn-icon" onClick={() => openEdit(r)}><FiEdit2 /></button>}
                    {hasPermission('delete-departments') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editing ? 'Edit Department' : 'Add Department'}
        size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Department Name <span className="req">*</span></label>
          <input
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            value={form.name}
            onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({}); }}
            placeholder="e.g. Sale, Production, HR"
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>
        <div className="form-group" style={{ marginTop: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            <span className="form-label" style={{ marginBottom: 0 }}>Active</span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
